import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import messagingService from '../../services/messagingService';
import CollaborativeDocumentEditor from './components/CollaborativeDocumentEditor';

const SERVICE_LABELS = {
  architecte: 'Architecte',
  geometre: 'Géomètre',
  decorateur: 'Décorateur',
  demenageur: 'Déménageur',
  nettoyage: 'Nettoyage',
  electricien: 'Électricien',
  plombier: 'Plombier',
  menuisier: 'Menuisier',
  climatisation: 'Climatisation',
  securite: 'Sécurité',
};

const FILE_ICONS = {
  'image/jpeg': 'Image',
  'image/png': 'Image',
  'image/gif': 'Image',
  'image/webp': 'Image',
  'application/pdf': 'FileText',
  'video/mp4': 'Video',
  'video/quicktime': 'Video',
  'text/plain': 'FileText',
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return date?.toLocaleDateString('fr-FR', { weekday: 'short' });
  return date?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} Mo`;
};

// ─── New Conversation Modal ───────────────────────────────────────────────────
const NewConversationModal = ({ onClose, onCreate, bookings }) => {
  const [subject, setSubject] = useState('');
  const [selectedBooking, setSelectedBooking] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!subject?.trim()) return;
    setLoading(true);
    await onCreate({ subject: subject?.trim(), bookingId: selectedBooking || null });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Nouvelle conversation</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Sujet *</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e?.target?.value)}
              placeholder="Ex: Question sur ma réservation d'architecte"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
          </div>
          {bookings?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Lier à une réservation (optionnel)</label>
              <select
                value={selectedBooking}
                onChange={e => setSelectedBooking(e?.target?.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white"
              >
                <option value="">Aucune réservation</option>
                {bookings?.map(b => (
                  <option key={b?.id} value={b?.id}>
                    {SERVICE_LABELS?.[b?.service_type] || b?.service_type} — {b?.preferred_date || 'Date à confirmer'}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleCreate}
            disabled={!subject?.trim() || loading}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Plus" size={16} />}
            Créer
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Attachment Preview ───────────────────────────────────────────────────────
const AttachmentItem = ({ attachment }) => {
  const [url, setUrl] = useState(null);
  const isImage = attachment?.file_type?.startsWith('image/');

  useEffect(() => {
    if (attachment?.file_path) {
      messagingService?.getAttachmentUrl(attachment?.file_path)?.then(({ url }) => setUrl(url));
    }
  }, [attachment?.file_path]);

  const iconName = FILE_ICONS?.[attachment?.file_type] || 'Paperclip';

  if (isImage && url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block mt-2">
        <img
          src={url}
          alt={attachment?.file_name}
          className="max-w-[200px] max-h-[150px] rounded-xl object-cover border border-white/20 hover:opacity-90 transition-opacity"
        />
      </a>
    );
  }

  return (
    <a
      href={url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 mt-2 px-3 py-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors max-w-[220px]"
    >
      <Icon name={iconName} size={16} className="flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-medium truncate">{attachment?.file_name}</p>
        {attachment?.file_size && (
          <p className="text-[10px] opacity-70">{formatFileSize(attachment?.file_size)}</p>
        )}
      </div>
      <Icon name="Download" size={14} className="flex-shrink-0 ml-auto" />
    </a>
  );
};

// ─── Message Bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ message, isOwn }) => {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isOwn && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 mt-1">
          {message?.sender_name?.[0]?.toUpperCase() || 'U'}
        </div>
      )}
      <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
        {!isOwn && (
          <span className="text-xs text-gray-500 mb-1 ml-1">{message?.sender_name}</span>
        )}
        <div
          className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
            isOwn
              ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-tr-sm' :'bg-white border border-gray-100 text-gray-800 rounded-tl-sm shadow-sm'
          }`}
        >
          {message?.content && <p className="whitespace-pre-wrap">{message?.content}</p>}
          {message?.message_attachments?.map(att => (
            <AttachmentItem key={att?.id} attachment={att} />
          ))}
        </div>
        <span className={`text-[10px] text-gray-400 mt-1 ${isOwn ? 'mr-1' : 'ml-1'}`}>
          {formatTime(message?.created_at)}
          {isOwn && (
            <Icon
              name={message?.is_read ? 'CheckCheck' : 'Check'}
              size={11}
              className={`inline ml-1 ${message?.is_read ? 'text-amber-500' : 'text-gray-400'}`}
            />
          )}
        </span>
      </div>
    </div>
  );
};

// ─── Conversation List Item ───────────────────────────────────────────────────
const ConversationItem = ({ conv, isActive, onClick, currentUserId }) => {
  const lastMsg = conv?.messages?.[conv?.messages?.length - 1];
  const unreadCount = conv?.messages?.filter(m => !m?.is_read && m?.sender_id !== currentUserId)?.length || 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-4 border-b border-gray-50 hover:bg-amber-50/50 transition-colors ${
        isActive ? 'bg-amber-50 border-l-2 border-l-amber-500' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
          <Icon name="MessageSquare" size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-0.5">
            <p className={`text-sm truncate ${isActive ? 'font-semibold text-amber-700' : 'font-medium text-gray-900'}`}>
              {conv?.subject || 'Conversation'}
            </p>
            <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
              {formatTime(conv?.last_message_at)}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {lastMsg?.content || 'Aucun message'}
          </p>
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center mt-1 min-w-[18px] h-[18px] px-1 bg-amber-500 text-white text-[10px] font-bold rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

// ─── Main Messaging Page ──────────────────────────────────────────────────────
const InAppMessaging = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewConvModal, setShowNewConvModal] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [error, setError] = useState(null);
  const [showMobileList, setShowMobileList] = useState(true);
  const [activePanel, setActivePanel] = useState('messages'); // 'messages' | 'documents'

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const channelRef = useRef(null);
  const textareaRef = useRef(null);

  const activeConv = conversations?.find(c => c?.id === activeConvId);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!user) return;
    setLoadingConvs(true);
    const { data } = await messagingService?.getConversations();
    setConversations(data || []);
    setLoadingConvs(false);
  }, [user]);

  // Load messages for active conversation
  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    setLoadingMsgs(true);
    const { data } = await messagingService?.getMessages(convId);
    setMessages(data || []);
    setLoadingMsgs(false);
    await messagingService?.markAsRead(convId);
    setTimeout(scrollToBottom, 100);
  }, [scrollToBottom]);

  // Load bookings for new conversation modal
  useEffect(() => {
    if (!user) return;
    import('../professional-services-booking/index.jsx')?.catch(() => {});
    // Load bookings from supabase
    import('../../services/professionalBookingService')?.then(mod => {
      mod?.default?.getUserBookings()?.then(({ data }) => setBookings(data || []));
    });
  }, [user]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Subscribe to real-time messages
  useEffect(() => {
    if (!activeConvId) return;

    // Cleanup previous channel
    if (channelRef?.current) {
      messagingService?.unsubscribe(channelRef?.current);
      channelRef.current = null;
    }

    channelRef.current = messagingService?.subscribeToMessages(activeConvId, (newMsg) => {
      setMessages(prev => {
        if (prev?.find(m => m?.id === newMsg?.id)) return prev;
        return [...prev, { ...newMsg, message_attachments: [] }];
      });
      setTimeout(scrollToBottom, 50);
      // Refresh conversations to update last message
      loadConversations();
    });

    return () => {
      if (channelRef?.current) {
        messagingService?.unsubscribe(channelRef?.current);
        channelRef.current = null;
      }
    };
  }, [activeConvId, scrollToBottom, loadConversations]);

  const handleSelectConversation = (convId) => {
    setActiveConvId(convId);
    setShowMobileList(false);
    loadMessages(convId);
  };

  const handleSendMessage = async () => {
    if (!newMessage?.trim() || !activeConvId || sending) return;
    setSending(true);
    setError(null);

    const content = newMessage?.trim();
    setNewMessage('');

    const { data, error: sendError } = await messagingService?.sendMessage({
      conversationId: activeConvId,
      content,
    });

    setSending(false);

    if (sendError) {
      setError(sendError);
      setNewMessage(content);
      return;
    }

    if (data) {
      setMessages(prev => {
        if (prev?.find(m => m?.id === data?.id)) return prev;
        return [...prev, { ...data, message_attachments: [] }];
      });
      setTimeout(scrollToBottom, 50);
      loadConversations();
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !activeConvId || !user) return;

    setUploadingFile(true);
    setError(null);

    // First send a message to get message ID
    const { data: msgData, error: msgError } = await messagingService?.sendMessage({
      conversationId: activeConvId,
      content: '',
      messageType: 'file',
    });

    if (msgError) {
      setError(msgError);
      setUploadingFile(false);
      return;
    }

    // Upload file
    const { data: uploadData, error: uploadError } = await messagingService?.uploadAttachment(file, user?.id);

    if (uploadError) {
      setError(uploadError);
      setUploadingFile(false);
      return;
    }

    // Save attachment record
    await messagingService?.saveAttachment({
      messageId: msgData?.id,
      fileName: file?.name,
      filePath: uploadData?.path,
      fileSize: file?.size,
      fileType: file?.type,
    });

    // Reload messages to get attachment
    await loadMessages(activeConvId);
    setUploadingFile(false);
    loadConversations();

    // Reset file input
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleCreateConversation = async ({ subject, bookingId }) => {
    const { data, error: createError } = await messagingService?.createConversation({
      subject,
      bookingId,
    });

    if (createError) {
      setError(createError);
      return;
    }

    setShowNewConvModal(false);
    await loadConversations();
    if (data?.id) {
      handleSelectConversation(data?.id);
    }
  };

  if (!user) {
    return (
      <>
        <Helmet>
          <title>Messagerie | Kapital States</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
          <Header />
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
              <Icon name="MessageSquare" size={32} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connexion requise</h2>
            <p className="text-gray-500 text-center mb-6">Connectez-vous pour accéder à votre messagerie.</p>
            <Link
              to="/auth"
              className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-all"
            >
              Se connecter
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Messagerie | Kapital States</title>
        <meta name="description" content="Communiquez avec vos professionnels avant, pendant et après vos réservations." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30 flex flex-col">
        <Header />

        <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 py-6 gap-4">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Icon name="MessageSquare" size={24} className="text-amber-500" />
                Messagerie
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">Communiquez avec vos professionnels</p>
            </div>
            <button
              onClick={() => setShowNewConvModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm"
            >
              <Icon name="Plus" size={16} />
              <span className="hidden sm:inline">Nouvelle conversation</span>
            </button>
          </div>

          {/* Main Chat Layout */}
          <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex" style={{ minHeight: '600px' }}>
            {/* Conversation List */}
            <div className={`w-full md:w-80 lg:w-96 border-r border-gray-100 flex flex-col flex-shrink-0 ${!showMobileList ? 'hidden md:flex' : 'flex'}`}>
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Conversations ({conversations?.length})
                </p>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loadingConvs ? (
                  <div className="flex flex-col items-center justify-center h-40 gap-3">
                    <Icon name="Loader2" size={24} className="text-amber-500 animate-spin" />
                    <p className="text-sm text-gray-400">Chargement...</p>
                  </div>
                ) : conversations?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-48 px-6 text-center">
                    <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center mb-3">
                      <Icon name="MessageSquarePlus" size={22} className="text-amber-500" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Aucune conversation</p>
                    <p className="text-xs text-gray-400">Créez une nouvelle conversation pour commencer</p>
                  </div>
                ) : (
                  conversations?.map(conv => (
                    <ConversationItem
                      key={conv?.id}
                      conv={conv}
                      isActive={conv?.id === activeConvId}
                      onClick={() => handleSelectConversation(conv?.id)}
                      currentUserId={user?.id}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Chat Window */}
            <div className={`flex-1 flex flex-col ${showMobileList ? 'hidden md:flex' : 'flex'}`}>
              {!activeConvId ? (
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-3xl flex items-center justify-center mb-4">
                    <Icon name="MessageSquare" size={36} className="text-amber-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Sélectionnez une conversation</h3>
                  <p className="text-sm text-gray-400 max-w-xs">
                    Choisissez une conversation dans la liste ou créez-en une nouvelle pour commencer à communiquer.
                  </p>
                </div>
              ) : (
                <>
                  {/* Chat Header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white">
                    <button
                      onClick={() => setShowMobileList(true)}
                      className="md:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Icon name="ArrowLeft" size={18} />
                    </button>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white flex-shrink-0">
                      <Icon name="MessageSquare" size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {activeConv?.subject || 'Conversation'}
                      </p>
                      {activeConv?.booking_id && (
                        <p className="text-xs text-gray-400">Liée à une réservation</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Panel toggle */}
                      <div className="flex items-center bg-gray-100 rounded-lg p-0.5 mr-1">
                        <button
                          onClick={() => setActivePanel('messages')}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                            activePanel === 'messages' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Icon name="MessageSquare" size={13} />
                          Messages
                        </button>
                        <button
                          onClick={() => setActivePanel('documents')}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                            activePanel === 'documents' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Icon name="Files" size={13} />
                          Documents
                        </button>
                      </div>
                      <button
                        onClick={() => loadMessages(activeConvId)}
                        className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                        title="Actualiser"
                      >
                        <Icon name="RefreshCw" size={15} className="text-gray-500" />
                      </button>
                    </div>
                  </div>

                  {/* Panel Content */}
                  {activePanel === 'documents' ? (
                    <div className="flex-1 overflow-hidden">
                      <CollaborativeDocumentEditor conversationId={activeConvId} escrowTransactionId={activeConv?.booking_id || null} />
                    </div>
                  ) : (
                    <>
                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50/30">
                    {loadingMsgs ? (
                      <div className="flex items-center justify-center h-32">
                        <Icon name="Loader2" size={24} className="text-amber-500 animate-spin" />
                      </div>
                    ) : messages?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-32 text-center">
                        <Icon name="MessageCircle" size={28} className="text-gray-300 mb-2" />
                        <p className="text-sm text-gray-400">Aucun message. Commencez la conversation !</p>
                      </div>
                    ) : (
                      <>
                        {messages?.map((msg, idx) => {
                          const prevMsg = messages?.[idx - 1];
                          const showDateSep = !prevMsg || new Date(msg?.created_at)?.toDateString() !== new Date(prevMsg?.created_at)?.toDateString();
                          return (
                            <React.Fragment key={msg?.id}>
                              {showDateSep && (
                                <div className="flex items-center gap-3 my-4">
                                  <div className="flex-1 h-px bg-gray-200" />
                                  <span className="text-[10px] text-gray-400 font-medium px-2 bg-gray-50/30">
                                    {new Date(msg?.created_at)?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                                  </span>
                                  <div className="flex-1 h-px bg-gray-200" />
                                </div>
                              )}
                              <MessageBubble
                                message={msg}
                                isOwn={msg?.sender_id === user?.id}
                              />
                            </React.Fragment>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Error Banner */}
                  {error && (
                    <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2">
                      <Icon name="AlertCircle" size={14} className="text-red-500 flex-shrink-0" />
                      <p className="text-xs text-red-600 flex-1">{error}</p>
                      <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  )}

                  {/* Message Input */}
                  <div className="px-4 py-3 border-t border-gray-100 bg-white">
                    <div className="flex items-end gap-2">
                      {/* File Upload */}
                      <button
                        onClick={() => fileInputRef?.current?.click()}
                        disabled={uploadingFile}
                        className="p-2.5 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-xl transition-colors flex-shrink-0 disabled:opacity-50"
                        title="Joindre un fichier"
                      >
                        {uploadingFile ? (
                          <Icon name="Loader2" size={18} className="animate-spin" />
                        ) : (
                          <Icon name="Paperclip" size={18} />
                        )}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx,.txt,video/mp4,video/quicktime"
                        onChange={handleFileSelect}
                      />

                      {/* Text Input */}
                      <div className="flex-1 relative">
                        <textarea
                          ref={textareaRef}
                          value={newMessage}
                          onChange={e => setNewMessage(e?.target?.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Écrivez votre message... (Entrée pour envoyer)"
                          rows={1}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none max-h-32 overflow-y-auto"
                          style={{ minHeight: '44px' }}
                        />
                      </div>

                      {/* Send Button */}
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage?.trim() || sending}
                        className="p-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 shadow-sm"
                      >
                        {sending ? (
                          <Icon name="Loader2" size={18} className="animate-spin" />
                        ) : (
                          <Icon name="Send" size={18} />
                        )}
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1.5 ml-12">
                      Maj+Entrée pour nouvelle ligne • Images, PDF, vidéos acceptés
                    </p>
                  </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {showNewConvModal && (
        <NewConversationModal
          onClose={() => setShowNewConvModal(false)}
          onCreate={handleCreateConversation}
          bookings={bookings}
        />
      )}
    </>
  );
};

export default InAppMessaging;
